import * as React from 'react';
import cx from 'classnames';

export interface NamedPointSeries {
  name: string;
  cumulativePoints: number[];
}

export interface HourlyPointsBlock {
  timezone: string;
  startsAt: string;
  endsAt: string;
  hours: string[];
  departmentSeries: NamedPointSeries[];
  divisionSeries: NamedPointSeries[];
  participantSeries?: NamedPointSeries[];
}

export type HourlyChartView = 'department' | 'division' | 'participant';

const CHART_COLORS = [
  '#068067', '#0a9b7a', '#2c7873', '#4a90a4', '#6b5b95',
  '#c05746', '#d4a017', '#5d737e', '#118ab2', '#073b4c',
  '#ef476f', '#ffd166', '#06d6a0', '#8338ec', '#fb5607'
];

const colorForIndex = (i: number) => CHART_COLORS[i % CHART_COLORS.length];

interface Props {
  block: HourlyPointsBlock;
  view: HourlyChartView;
  teamLabel: string;
  compact?: boolean;
  maxSeries?: number;
}

interface State {
  hoverIndex: number | null;
}

export default class HourlyPointsChart extends React.Component<Props, State> {
  public state: State = { hoverIndex: null };

  public componentDidUpdate(prevProps: Props) {
    if (prevProps.view !== this.props.view || prevProps.compact !== this.props.compact) {
      this.setState({ hoverIndex: null });
    }
  }

  private setHover = (hoverIndex: number | null) => {
    this.setState({ hoverIndex });
  }

  private sliceSeries(series: NamedPointSeries[]): NamedPointSeries[] {
    const { maxSeries, compact } = this.props;
    const limit = maxSeries || (compact ? 10 : series.length);
    if (series.length <= limit) {
      return series;
    }
    return series.slice(0, limit);
  }

  public render() {
    const { block, view, teamLabel, compact } = this.props;
    const { hoverIndex } = this.state;
    const raw = (() => {
      if (view === 'participant') {
        return block.participantSeries || [];
      }
      if (view === 'division') {
        return block.divisionSeries;
      }
      return block.departmentSeries;
    })();
    const series = this.sliceSeries(raw || []);
    const hours = block.hours || [];

    if (!hours.length) {
      return <p className="cc__admin_totals__meta">No hourly data for the challenge window yet.</p>;
    }
    if (!series.length) {
      return <p className="cc__admin_totals__meta">No points recorded in this period.</p>;
    }

    const width = compact ? 340 : 960;
    const height = compact ? 200 : 380;
    const padL = compact ? 44 : 56;
    const padR = compact ? 8 : 16;
    const padT = compact ? 8 : 12;
    const padB = compact ? 32 : 48;
    const innerW = width - padL - padR;
    const innerH = height - padT - padB;

    let yMax = 1;
    series.forEach((s) => {
      const pts = s.cumulativePoints || [];
      if (pts.length) {
        yMax = Math.max(yMax, pts[pts.length - 1]);
      }
    });

    const xAt = (i: number) => padL + (hours.length <= 1 ? innerW / 2 : (i / (hours.length - 1)) * innerW);
    const yAt = (v: number) => padT + innerH - (v / yMax) * innerH;

    const tickCount = compact ? 3 : 5;
    const yTicks: number[] = [];
    for (let t = 0; t <= tickCount; t++) {
      yTicks.push(Math.round((yMax * t) / tickCount));
    }

    const labelEvery = Math.max(1, Math.ceil(hours.length / (compact ? 4 : 8)));
    const dimOthers = hoverIndex !== null;

    const pathFor = (s: NamedPointSeries) => {
      const pts = s.cumulativePoints || [];
      return pts
        .map((v, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(2)} ${yAt(v).toFixed(2)}`)
        .join(' ');
    };

    const hitOrder = series.map((_, si) => si).reverse();

    return (
      <div className={cx('cc__admin_totals__timeseries', { 'cc__admin_totals__timeseries--compact': compact })}>
        {!compact &&
          <p className="cc__admin_totals__meta">
            Cumulative total points by hour ({block.timezone || 'America/Los_Angeles'}).
            {view === 'department' ? ' One line per department.' : view === 'participant' ? ' One line per participant.' : ` One line per ${teamLabel.toLowerCase()}.`}
            {' '}Hover a legend label or line to highlight.
          </p>
        }
        {compact &&
          <p className="cc__admin_totals__meta cc__scoreboard_trend__caption">
            {view === 'participant'
              ? 'Top participants by cumulative points (Pacific time). Hover legend or lines.'
              : view === 'division'
                ? `Top ${teamLabel.toLowerCase()} leaders by cumulative points (Pacific time). Hover legend or lines.`
                : 'Top departments by cumulative points (Pacific time). Hover legend or lines.'}
          </p>
        }
        <svg
          className="cc__admin_totals__timeseries__svg"
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="Cumulative points over time"
          onMouseLeave={() => this.setHover(null)}
        >
          {yTicks.map((tick) => (
            <g key={tick}>
              <line
                x1={padL}
                x2={width - padR}
                y1={yAt(tick)}
                y2={yAt(tick)}
                className="cc__admin_totals__timeseries__grid"
              />
              <text x={padL - 6} y={yAt(tick) + 4} textAnchor="end" className="cc__admin_totals__timeseries__tick">
                {compact && tick >= 1000 ? `${Math.round(tick / 1000)}k` : tick}
              </text>
            </g>
          ))}
          {series.map((s, si) => {
            const pts = s.cumulativePoints || [];
            const d = pathFor(s);
            const active = hoverIndex === si;
            const faded = dimOthers && !active;
            const baseWidth = series.length > 20 ? 1.2 : (compact ? 1.5 : 2);
            const strokeW = active ? baseWidth + 2 : baseWidth;
            const opacity = faded ? 0.12 : (series.length > 25 && !active ? 0.85 : 1);
            return (
              <path
                key={`line-${si}-${s.name}`}
                d={d}
                fill="none"
                stroke={colorForIndex(si)}
                strokeWidth={strokeW}
                opacity={opacity}
                pointerEvents="none"
                className={cx('cc__admin_totals__timeseries__line', { 'is-active': active })}
              >
                <title>{`${s.name}: ${pts.length ? pts[pts.length - 1] : 0} pts total`}</title>
              </path>
            );
          })}
          {hitOrder.map((si) => {
            const s = series[si];
            const d = pathFor(s);
            return (
              <path
                key={`hit-${si}-${s.name}`}
                d={d}
                fill="none"
                stroke="transparent"
                strokeWidth={compact ? 10 : 12}
                className="cc__admin_totals__timeseries__hit"
                onMouseEnter={() => this.setHover(si)}
                aria-hidden={true}
              />
            );
          })}
          {hours.map((h, i) => {
            if (i % labelEvery !== 0 && i !== hours.length - 1) {
              return null;
            }
            const label = compact
              ? (h.length >= 10 ? h.slice(5, 10) : h)
              : (h.length >= 16 ? `${h.slice(5, 10)} ${h.slice(11, 16)}` : h);
            return (
              <text
                key={`${h}-${i}`}
                x={xAt(i)}
                y={height - (compact ? 10 : 16)}
                textAnchor="middle"
                className="cc__admin_totals__timeseries__tick"
              >
                {label}
              </text>
            );
          })}
        </svg>
        <ul
          className="cc__admin_totals__timeseries__legend"
          onMouseLeave={() => this.setHover(null)}
        >
          {series.map((s, si) => {
            const pts = s.cumulativePoints || [];
            const total = pts.length ? pts[pts.length - 1] : 0;
            const active = hoverIndex === si;
            const faded = dimOthers && !active;
            return (
              <li
                key={`legend-${si}-${s.name}`}
                className={cx('cc__admin_totals__timeseries__legenditem', {
                  'is-active': active,
                  'is-dimmed': faded
                })}
                onMouseEnter={() => this.setHover(si)}
                onFocus={() => this.setHover(si)}
                onBlur={() => this.setHover(null)}
                tabIndex={0}
                title={`${s.name}: ${total} points`}
              >
                <span
                  className="cc__admin_totals__timeseries__swatch"
                  style={{ background: colorForIndex(si) }}
                />
                <span className="cc__admin_totals__timeseries__legendlabel">
                  {compact ? s.name : `${s.name} (${total} pts)`}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }
}
